import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ButtonPrimary } from "../../components/utils";
import convertToBase64 from "../../utils/convertToBase64";
import {
  useGetTransactionTemplate,
  useParseTransactionUpload,
} from "../../queries/bulkGrantUpdate";
import { ParsedTransactionRow } from "../../types/BulkGrantUpdate";
import BulkGrantUpdatePreview from "./BulkGrantUpdatePreview";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";

interface BulkGrantUpdateProps {
  selectedGrantIds?: string[];
}

const BulkGrantUpdate: React.FC<BulkGrantUpdateProps> = ({
  selectedGrantIds = [],
}) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ParsedTransactionRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { mutate: downloadTemplate, isLoading: downloadingTemplate } =
    useGetTransactionTemplate();

  const { mutate: parseUpload, isLoading: parsingData } =
    useParseTransactionUpload();

  const handleDownloadTemplate = async () => {
    downloadTemplate(selectedGrantIds, {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "grant-transaction-template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Template downloaded successfully");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Error downloading template"
        );
      },
    });
  };

  const handlePreview = async () => {
    if (!file) return;
    const base64File = (await convertToBase64(file)) as string;
    // Remove the data URL prefix if present
    const base64Content = base64File.includes(",")
      ? base64File.split(",")[1]
      : base64File;

    parseUpload(base64Content, {
      onSuccess: (data) => {
        if (
          data.errors &&
          data.errors.length > 0 &&
          (!data.data || data.data.length === 0)
        ) {
          toast.error(
            data.errors?.[0] || "The uploaded file contains no valid data."
          );
          setErrors(data.errors || []);
          return;
        }
        setPreviewData(data.data);
        if (data.errors && data.errors.length > 0) {
          setErrors(data.errors);
        }
        setShowPreview(true);
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Error parsing transaction file"
        );
      },
    });
  };

  const handleFileDrop = async (file: File) => {
    setFile(file);
    setErrors([]);
  };

  if (showPreview) {
    return (
      <BulkGrantUpdatePreview
        data={previewData}
        onCancel={() => {
          setShowPreview(false);
          setPreviewData([]);
          setFile(undefined);
          setErrors([]);
        }}
        onSuccess={() => {
          navigate("/options/allPlans");
        }}
      />
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Bulk Update Grant Transactions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel file to bulk update grant transactions (Lapsed,
            Exercise, Surrendered, etc.)
          </p>
        </div>
        <ButtonPrimary
          className="flex items-center gap-2"
          onClick={handleDownloadTemplate}
          loading={downloadingTemplate}
        >
          <Icon icon="mdi:download-outline" height={18} />
          Download Template
        </ButtonPrimary>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h3 className="text-xl font-medium text-gray-900">Upload File</h3>
            <p className="text-gray-500">
              Download the template, fill in the transaction details, and upload
              it here.
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto">
            <FileDropzone
              onFileSelect={handleFileDrop}
              selectedFile={file}
              onClear={() => {
                setFile(undefined);
                setErrors([]);
              }}
            />

            {errors.length > 0 && (
              <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded-md">
                <div className="flex items-center gap-2 mb-2 text-red-700">
                  <Icon icon="material-symbols:error-outline" height={20} />
                  <span className="font-semibold">
                    {errors.length} Errors Found
                  </span>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-600 max-h-40 overflow-y-auto pl-2">
                  {errors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <Instructions
            data={[
              "Use the downloaded template to ensure correct format",
              "Valid transaction types: LAPSED, CASHED OUT, STOCK EXERCISE, SOLD, SURRENDERED",
              "SURRENDERED is an optional transaction sub type supported with LAPSED transactions",
              "Transaction date should be in YYYY-MM-DD format",
              "Buyer Name is required only for SOLD transactions",
              "STOCK EXERCISE and SOLD should be in integers only (no fractions). If provided as fractions, they will be rounded down",
              'Refer to the "Grant Reference" sheet for current option status',
            ]}
          />

          {file && (
            <div className="flex justify-center pt-4">
              <ButtonPrimary
                className="h-10 text-base shadow-md hover:shadow-lg transition-shadow"
                onClick={handlePreview}
                disabled={parsingData}
              >
                {parsingData ? (
                  <div className="flex items-center gap-2">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Preview Changes"
                )}
              </ButtonPrimary>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkGrantUpdate;
